import { Device, TwilioError, Call } from '@twilio/voice-sdk';

import { Beech } from './beech';

export default class BeechTwilio {
	private beech: Beech;

	private unpersisted: boolean;

	private ready: boolean;
	private callerId: string | null;
	private device?: Device;
	private call: Call | null = null;

	private static readonly DEVICE_LOST_GRACE_MS = 15000;
	private deviceLostTimer: number | null = null;

	private static readonly DEVICE_ENUMERATION_TIMEOUT_MS = 10000;

	constructor(beech: Beech) {
		logg.log('twilio >> init');

		this.beech = beech;
		this.unpersisted = false;
		this.ready = false;

		// Default: unset → outbound number is resolved automatically by dialed prefix.
		this.callerId = null;
	}

	public setup = async (token: string) => {
		logg.log('twilio >> setup >> us1');

		if (this.device) {
			logg.log('twilio >> setup >> updateToken');
			await this.device.updateToken(token);
			return;
		}

		this.device = new Device(token, {
			logLevel: 'info',
			enableImprovedSignalingErrorPrecision: true
		});
		this.device.on('unregistered', this.onUnregistered);
		this.device.on('registered', this.onReady);
		this.device.on('incoming', this.onIncoming);
		this.device.on('error', this.onError);

		await this.device.register();

		// unofficial - Intercept internal publisher events
		let publisher = (<any>this.device)._publisher;
		if (typeof publisher !== 'undefined' && typeof publisher.orig__post === 'undefined') {
			let onInternalEvent = this.onInternalEvent;
			let TwilioEventPublisher = Object.getPrototypeOf(publisher);

			TwilioEventPublisher.orig__post = TwilioEventPublisher._post;
			TwilioEventPublisher._post = function (
				this: any,
				endpointName: string,
				level: string,
				group: string,
				name: string,
				payload: any,
				call: Call,
				force: boolean
			) {
				onInternalEvent(this, endpointName, level, group, name, payload, call, force);
			};
		}
	};

	public getDevice = (): Device | undefined => {
		return this.device;
	};

	public doAccept = () => {
		if (this.device?.isBusy) {
			throw Error('twilio >> doAccept is busy!');
		}

		if (!this.call) {
			throw Error('twilio >> doAccept no call!');
		}

		this.call.accept();
	};

	public doReject = () => {
		if (!this.call) {
			throw Error('twilio >> doReject no active call!');
		}

		this.call?.reject();
	};

	public doHangup = () => {
		if (!this.call) {
			throw Error('twilio >> doHangup no active call!');
		}

		this.call?.disconnect();
	};

	public doOutgoingDial = async (number: string) => {
		const device = this.device;
		if (!device) {
			throw Error('twilio >> doOutgoingDial no device!');
		}

		if (device.isBusy) {
			throw Error('twilio >> doOutgoingDial already has an active call!');
		}

		// banyan owns caller-ID routing. Send only the agent's explicit override (when set);
		// with none, banyan auto-routes by destination.
		this.call = await device.connect({
			params: {
				DialTo: number,
				...(this.callerId ? { CallerIdOverride: this.callerId } : {})
			}
		});

		this.beech.setLastDialed(number);

		logg.log('twilio >> doOutgoingDial', this.call);

		this.setCallHandlers(this.call);

		return this.call;
	};

	public sendDigits = (digits: string) => {
		if (!this.call) {
			logg.warn('twilio >> sendDigits no active call!');
			return;
		}

		this.call.sendDigits(digits);
	};

	public handleMute = () => {
		if (!this.call) {
			logg.warn('twilio >> handleMute no active call!');
			return;
		}

		this.call.mute(this.beech.model.getAgentMuted());
	};

	private onReady = () => {
		logg.log('twilio >> ready');
		logg.log('twilio >> ready >> device', this.device?.edge);

		if (this.deviceLostTimer != null) {
			window.clearTimeout(this.deviceLostTimer);
			this.deviceLostTimer = null;
		}

		this.beech.flow.onTwilioReady();

		this.debugAudioDevices();

		this.device?.audio?.off('deviceChange', this.onDeviceChange);
		this.device?.audio?.on('deviceChange', this.onDeviceChange);

		window.setTimeout(() => {
			void this.unpersist();

			$(document).trigger('gpsh:twilio.ready');
			this.ready = true;
		}, 1000);
	};

	private onDeviceChange = () => {
		logg.log('twilio >> deviceChange');

		this.debugAudioDevices();
	};

	private debugAudioDevices = () => {
		let inputDevices: any[] = [];
		this.device?.audio?.availableInputDevices.forEach((item: any) => {
			inputDevices.push({ id: item.deviceId, label: item.label });
		});

		let outputDevices: any[] = [];
		this.device?.audio?.availableOutputDevices.forEach((item: any) => {
			outputDevices.push({ id: item.deviceId, label: item.label });
		});

		// Debug devices
		logg.debug('twilio >> availableOutputDevices ' + JSON.stringify(outputDevices));
	};

	private onUnregistered = () => {
		logg.log('twilio >> offline');

		if (!this.ready || this.deviceLostTimer != null) {
			return;
		}

		this.deviceLostTimer = window.setTimeout(() => {
			this.deviceLostTimer = null;
			if (!navigator.onLine) {
				return;
			}
			if (!this.isRegistered()) {
				this.beech.flow.onTwilioDeviceLost('device-offline');
			}
		}, BeechTwilio.DEVICE_LOST_GRACE_MS);
	};

	private onIncoming = (call: Call) => {
		logg.log('twilio >> incoming', call);

		this.call = call;

		this.setCallHandlers(call);

		this.handleMute();

		this.beech.flow.onTwilioIncoming(call);
	};

	private onCancel = (call: Call) => {
		logg.log('twilio >> call >> cancel', call);
		this.beech.flow.onTwilioDisconnect();
		logg.log('twilio >> call >> cancel >> flow disconnected', call);
		this.call = null;
	};

	private onConnect = (call: Call) => {
		logg.log('twilio >> call >> connect', call);
		this.beech.flow.onTwilioConnect();
		this.call = call;
	};

	private onDisconnect = (call: Call) => {
		logg.log('twilio >> disconnect', call);

		if (this.call === call) {
			logg.log('twilio >> disconnect >> disconnecting', call);
			this.beech.flow.onTwilioDisconnect();
			this.call = null;
		}
	};

	private onError = (error: TwilioError.TwilioError, call?: Call) => {
		logg.warn('twilio >> error', error);

		this.beech.server.logTwilioError(error.code, error.message);
	};

	public isReady = () => {
		return this.ready;
	};

	public isRegistered = () => {
		return this.device?.state === 'registered';
	};

	public getActiveRingtoneDeviceId = (): string | null => {
		const devices = this.device?.audio?.ringtoneDevices.get();
		const firstDevice = devices && devices.size > 0 ? devices.values().next().value : undefined;
		return firstDevice && firstDevice.deviceId ? firstDevice.deviceId : null;
	};

	public setActiveRingtoneDeviceId = (deviceId: string) => {
		if (this.unpersisted) {
			this.beech.persist.setRingDevice(deviceId);
			this.device?.audio?.ringtoneDevices.set(deviceId).then(() => {
				logg.log('twilio >> ringtone device changed');
				$(document).trigger('gpsh:twilio.change');
			});
		}
	};

	public getActiveSpeakerDeviceId = (): string | null => {
		let devices = this.device?.audio?.speakerDevices.get();
		const firstDevice = devices && devices.size > 0 ? devices.values().next().value : undefined;
		return firstDevice && firstDevice.deviceId ? firstDevice.deviceId : null;
	};

	public setActiveSpeakerDeviceId = (deviceId: string) => {
		if (this.unpersisted) {
			this.beech.persist.setSpeakerDevice(deviceId);
			this.device?.audio?.speakerDevices.set(deviceId).then(() => {
				logg.log('twilio >> speaker device changed');
				$(document).trigger('gpsh:twilio.change');
			});
		}
	};

	public getActiveInputDeviceId = (): string | null => {
		let device = this.device?.audio?.inputDevice;
		return !device ? null : device.deviceId;
	};

	public setActiveInputDeviceId = (deviceId: string) => {
		if (this.unpersisted) {
			this.beech.persist.setInputDevice(deviceId);
			this.device?.audio?.setInputDevice(deviceId).then(() => {
				logg.log('twilio >> input device changed');
				$(document).trigger('gpsh:twilio.change');
			});
		}
	};

	public getAvailableCallerIds = () => {
		return [
			{
				id: '+353818700700',
				label: 'HaloCare (+353818700700)'
			},
			{
				id: '+353599194040',
				label: 'HaloCare (+353599194040)'
			},
			{
				id: '+18882477102',
				label: 'HaloCare USA (+18882477102)'
			},
			{
				id: '+353599194205',
				label: 'HaloCare Test (+353599194205)'
			},
			{
				id: '+353599188304',
				label: 'Radius Dummy (+353599188304)'
			},
			{
				id: '+13132468119',
				label: 'HaloCare US Local (GrandPad) (+13132468119)'
			}
		];
	};

	public getCallerId = (): string | null => {
		return this.callerId;
	};

	public setCallerId = (callerId: string | null) => {
		if (callerId === null) {
			this.callerId = null;
			return;
		}

		if (this.isCallerIdValid(callerId)) {
			this.callerId = callerId;
		}
	};

	private isCallerIdValid = (callerId: string): boolean => {
		for (let item of this.getAvailableCallerIds()) {
			if (item.id == callerId) {
				return true;
			}
		}

		return false;
	};

	private setCallHandlers = (call: Call): void => {
		call.on('accept', () => {
			logg.log('twilio >> call >> accept');
		});

		call.on('cancel', this.onCancel);

		call.on('connect', this.onConnect);

		call.on('disconnect', this.onDisconnect);

		call.on('error', (error) => {
			logg.log('twilio >> call >> error', error);
		});

		call.on('mute', (muted) => {
			logg.log('twilio >> call >> mute', muted);
		});

		call.on('reconnecting', (error) => {
			logg.log('twilio >> call >> reconnecting', error);
		});

		call.on('reconnected', () => {
			logg.log('twilio >> call >> reconnected');
		});

		call.on('ringing', () => {
			logg.log('twilio >> call >> ringing');
		});

		call.on('warning', (name) => {
			logg.log('twilio >> call >> warning', name);
			this.onQualityWarning(call, name);
		});

		call.on('warning-cleared', (name) => {
			logg.log('twilio >> call >> warning-cleared', name);
			this.onQualityWarningCleared(call, name);
		});

		// unofficial
		this.beech.metrics.intercept(call);
	};

	private onQualityWarning = (call: Call, warningName: string) => {
		logg.log('twilio >> quality warning');

		if (['high-rtt', 'low-mos', 'high-jitter', 'high-packet-loss'].indexOf(warningName) >= 0) {
			// Quality related
			this.beech.model.addCallQualityWarning(warningName);
		}

		this.beech.server.logQualityWarning(call.parameters.CallSid, warningName, true);

		this.beech.notify.showQualityWarningNotification(warningName);
	};

	private onQualityWarningCleared = (call: Call, warningName: string) => {
		logg.log('twilio >> quality warning cleared');

		this.beech.model.removeCallQualityWarning(warningName);
		this.beech.server.logQualityWarning(call.parameters.CallSid, warningName, false);

		this.beech.notify.hideQualityWarningNotification(warningName);
	};

	private onInternalEvent = (
		publisher: any,
		endpointName: string,
		level: string,
		group: string,
		name: string,
		payload: any,
		call: Call,
		force: boolean
	) => {
		logg.debug('twilio >> event', endpointName, level, group, name, payload, call, force);

		// If we want to have Twilio post events to their own Analytics server
		if (this.beech.persist.getForwardTwilioInsights()) {
			publisher.orig__post(endpointName, level, group, name, payload, call, force);
		}
	};

	// Twilio can register before the OS has enumerated audio devices, so restoring a
	// saved device on a fixed delay races on slower machines and set()/setInputDevice()
	// throws "Devices not found". Wait for the list, then restore only devices that are
	// present; anything missing falls back to the system default rather than erroring.
	private unpersist = async () => {
		// Migration: older builds persisted caller-ID overrides that now hijack routing
		// (defaulted to +353818700700). Wipe them. TODO(HAL-377): remove after ~2026-08.
		this.beech.persist.clearCallerId();

		try {
			await this.waitForAudioDevices();

			const audio = this.device?.audio;
			const outputs = audio ? new Set(audio.availableOutputDevices.keys()) : new Set<string>();
			const inputs = audio ? new Set(audio.availableInputDevices.keys()) : new Set<string>();

			const ring = this.beech.persist.getRingDevice();
			if (ring && outputs.has(ring)) {
				await audio?.ringtoneDevices.set(ring);
			}

			const speaker = this.beech.persist.getSpeakerDevice();
			if (speaker && outputs.has(speaker)) {
				await audio?.speakerDevices.set(speaker);
			}

			const input = this.beech.persist.getInputDevice();
			if (input && inputs.has(input)) {
				await audio?.setInputDevice(input);
			}

			logg.log('twilio >> unpersist success');
		} catch (err) {
			logg.warn('twilio >> unpersist failed', err);
		} finally {
			this.unpersisted = true;

			$(document).trigger('gpsh:twilio.change');
			$(document).trigger('gpsh:twilio.unpersist');
		}
	};

	private waitForAudioDevices = async (): Promise<void> => {
		const audio = this.device?.audio;
		if (!audio) {
			return;
		}

		const deadline = Date.now() + BeechTwilio.DEVICE_ENUMERATION_TIMEOUT_MS;
		while (
			(audio.availableInputDevices.size === 0 || audio.availableOutputDevices.size === 0) &&
			Date.now() < deadline
		) {
			await new Promise((resolve) => window.setTimeout(resolve, 200));
		}
	};
}
